import React from 'react';
import {useTranslation} from 'react-i18next';
import {animated, config, useSpring} from 'react-spring';

import ErrorIcon from '@edna/icons/error.svg';
import InfoIcon from '@edna/icons/info.svg';
import SuccessIcon from '@edna/icons/success.svg';
import WarningIcon from '@edna/icons/warning.svg';
import {delay} from '@edna/utils';

import {useTheme} from '../styles';
import * as S from './style';
import {ENotificationType, TNotification, TNotificationType} from './types';

const iconMap: Record<TNotificationType, React.ComponentType<React.ComponentProps<'svg'>>> = {
  success: SuccessIcon,
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

export type TProps = TNotification & {
  removeNotification: TEmptyFunction;
};

const DEFAULT_DURATION = 5_000;

const Item = React.memo<TProps>(
  ({
    title,
    message,
    messageKey,
    messageArgs,
    type = 'info',
    removeNotification,
    lifeDuration = DEFAULT_DURATION,
  }) => {
    const {
      animation: {duration: themeDuration},
    } = useTheme();

    const ref = React.useRef<HTMLDivElement>(null);

    const [open, setOpen] = React.useState(false);

    const [lifeStyles, lifeStyleApi] = useSpring(() => ({
      right: '0%',
      config: {duration: lifeDuration},
    }));

    const styles = useSpring({
      from: {height: '0px', x: '100%', overflow: 'hidden'},
      to: open
        ? [
            {
              height: `${ref.current?.offsetHeight ?? 0}px`,
              config: {duration: themeDuration / 2},
            },
            {
              x: '0%',
              overflow: 'visible',
              config: config.gentle,
            },
          ]
        : {
            x: '100%',
            height: '0px',
            config: {duration: themeDuration},
          },
    });

    const handleClose = React.useCallback(async () => {
      setOpen(false);
      await delay(2 * themeDuration);
      removeNotification();
    }, [removeNotification, themeDuration]);

    const intervalRef = React.useRef<number | undefined>();

    const removeTimer = React.useCallback(() => {
      window.clearTimeout(intervalRef.current);
      lifeStyleApi.set({right: '0%'});
      lifeStyleApi.stop();
    }, [lifeStyleApi]);

    const startTimer = React.useCallback(() => {
      window.clearTimeout(intervalRef.current);
      intervalRef.current = window.setTimeout(handleClose, lifeDuration);
      lifeStyleApi.start({right: '100%'});
    }, [lifeDuration, handleClose, lifeStyleApi]);

    React.useEffect(() => {
      startTimer();
      setOpen(true);

      return removeTimer;
    }, [startTimer, removeTimer]);

    const {t} = useTranslation();
    const Icon = iconMap[type];
    const translatedMessage = messageKey ? t(messageKey, messageArgs) : message;

    return (
      <animated.div style={styles}>
        <S.ItemWrapper ref={ref}>
          <S.Item onMouseEnter={removeTimer} onMouseLeave={startTimer}>
            {Icon && <S.Icon as={Icon} type={type} />}
            <S.Header>
              {title && <S.Title>{title}</S.Title>}
              <S.Message>{translatedMessage}</S.Message>
            </S.Header>
            <S.CloseButton onClick={handleClose}>
              <S.CloseIcon />
            </S.CloseButton>
            <S.Life style={lifeStyles} type={type} />
          </S.Item>
        </S.ItemWrapper>
      </animated.div>
    );
  },
);

export {ENotificationType};

export default Item;
